import { Routes, Route, useNavigate } from "react-router-dom"
import React, { Suspense, useEffect } from "react"
import { APIProvider } from "@vis.gl/react-google-maps"
import Cancel from "./pages/Registration/components/Cancel"
import Layout from "./layouts/Layout"
import HomeLayout from "./layouts/HomeLayout"
import Missing from "./layouts/Missing"
import PersistLogin from "./pages/Login/utils/PersistLogin"
import Loading from "./layouts/Loading"
import { ErrorBoundary } from "react-error-boundary"
import ErrorFallback from "./layouts/ErrorFallback"
import { UserProvider } from "./contexts/UserContext"
import { useThemeMode } from "./contexts/ThemeContext"
import { ThemeProvider } from "@mui/material"
import { SearchProvider } from "./contexts/SearchContext"

const ContactUs = React.lazy(() => import("./pages/ContactUs/ContactUs"))
const Home = React.lazy(() => import("./pages/Home/Home"))
const Ports = React.lazy(() => import("./pages/Ports/Ports"))
const PortDataList = React.lazy(
    () => import("./pages/PortDataList/PortDataList")
)
const Data = React.lazy(() => import("./pages/DataItem/DataItem"))
const Profile = React.lazy(() => import("./pages/Profile/Profile"))
const PasswordReset = React.lazy(
    () => import("./pages/PasswordReset/PasswordReset")
)
const Welcome = React.lazy(() => import("./pages/Welcome/Welcome"))
const Privacy = React.lazy(() => import("./pages/Privacy/privacy"))

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY

export default function App() {
    const navigate = useNavigate()
    const { theme } = useThemeMode()

    useEffect(() => {
        document.body.style.backgroundColor = theme.palette.background.default
        document.body.style.backgroundImage = theme.palette.background.gradient
        document.body.style.backgroundAttachment = "fixed"
        document.body.style.backgroundSize = "cover"

        document.documentElement.style.backgroundColor =
            theme.palette.background.default
    }, [theme])

    return (
        <ThemeProvider theme={theme}>
            <ErrorBoundary
                FallbackComponent={ErrorFallback}
                onReset={() => navigate("/")}
            >
                <Suspense fallback={<Loading />}>
                    <SearchProvider>
                        <Routes>
                            {/* Public routes */}
                            <Route path="/" element={<HomeLayout />}>
                                <Route index element={<Home />} />
                                <Route path="confirm" element={<Welcome />} />
                                <Route
                                    path="resetPassword"
                                    element={<PasswordReset />}
                                />
                                <Route path="cancel" element={<Cancel />} />
                                <Route path="contact" element={<ContactUs />} />
                                <Route path="privacy" element={<Privacy />} />
                                <Route path="*" element={<Missing />} />
                            </Route>
                            {/* Protected routes */}
                            <Route path="/" element={<Layout />}>
                                <Route element={<PersistLogin />}>
                                    <Route
                                        path="ports"
                                        element={
                                            <APIProvider
                                                apiKey={GOOGLE_API_KEY}
                                            >
                                                <UserProvider>
                                                    <Ports />
                                                </UserProvider>
                                            </APIProvider>
                                        }
                                    />
                                    <Route
                                        path="ports/:code"
                                        element={
                                            <APIProvider
                                                apiKey={GOOGLE_API_KEY}
                                            >
                                                <UserProvider>
                                                    <PortDataList />
                                                </UserProvider>
                                            </APIProvider>
                                        }
                                    />
                                    <Route
                                        path="ports/:code/:name"
                                        element={
                                            <APIProvider
                                                apiKey={GOOGLE_API_KEY}
                                            >
                                                <UserProvider>
                                                    <Data />
                                                </UserProvider>
                                            </APIProvider>
                                        }
                                    />
                                    <Route
                                        path="User Profile"
                                        element={
                                            <UserProvider>
                                                <Profile />
                                            </UserProvider>
                                        }
                                    />
                                </Route>
                                <Route path="*" element={<Missing />} />
                            </Route>
                        </Routes>
                    </SearchProvider>
                </Suspense>
            </ErrorBoundary>
        </ThemeProvider>
    )
}
